import React from "react";
import { RefreshCw } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion, HTMLMotionProps } from "framer-motion";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const VARIANT_CLS: Record<Variant, string> = {
  primary: "bg-brand-500 text-white border border-transparent shadow-sm hover:bg-brand-600 hover:shadow-brand disabled:opacity-50 disabled:hover:shadow-none",
  secondary: "bg-surface text-ink-900 border border-ink-200 shadow-sm hover:bg-ink-100 hover:border-ink-300 disabled:opacity-50",
  ghost: "bg-transparent text-ink-700 border border-transparent hover:bg-ink-100 hover:text-ink-900 disabled:opacity-50",
  danger: "bg-surface text-danger-600 border border-danger-100 shadow-sm hover:bg-danger-50 disabled:opacity-50",
};

const SIZE_CLS: Record<Size, string> = {
  sm: "text-xs px-3 py-1.5 gap-1.5 rounded-md",
  md: "text-sm px-4 py-2 gap-2 rounded-lg",
  lg: "text-base px-6 py-3 gap-2 rounded-xl",
};

export interface ButtonProps extends Omit<HTMLMotionProps<"button">, "size" | "children"> {
  children?: React.ReactNode;
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  iconPosition?: "left" | "right";
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = "primary",
  size = "md",
  icon: Icon,
  iconPosition = "left",
  loading = false,
  fullWidth = false,
  disabled,
  className = "",
  children,
  ...rest
}, ref) => {
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-5 w-5" : "h-4 w-4";
  return (
    <motion.button
      ref={ref}
      whileHover={{ scale: disabled || loading ? 1 : 1.03 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.96 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center font-medium font-sans transition-colors duration-200 ease-out disabled:cursor-not-allowed",
        VARIANT_CLS[variant],
        SIZE_CLS[size],
        fullWidth ? "w-full" : "",
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {loading ? (
        <RefreshCw className={`${iconSize} animate-spin`} />
      ) : (
        Icon && iconPosition === "left" && <Icon className={iconSize} />
      )}
      {children}
      {!loading && Icon && iconPosition === "right" && <Icon className={iconSize} />}
    </motion.button>
  );
});

Button.displayName = "Button";
export default Button;
